import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { textSans } from '@guardian/src-foundations/typography';
import { neutral } from '@guardian/src-foundations/palette';
import { Button } from '@guardian/src-button';
import PlusIcon from '@frontend/static/icons/plus.svg';
import MinusIcon from '@frontend/static/icons/minus.svg';
import { decidePalette } from '@root/src/web/lib/decidePalette';
import { Form } from '../Callout/Form';
const wrapperStyles = css `
	margin-bottom: 26px;
	margin-top: 16px;
`;
const calloutDetailsStyles = css `
	border-top: 1px ${neutral[86]} solid;
	border-bottom: 1px ${neutral[86]} solid;
	position: relative;
	padding-bottom: 10px;

	/* IE does not support summary HTML elements, so we need to hide children ourself */
	:not([open]) > *:not(summary) {
		display: none;
	}
`;
const backgroundColorStyle = css `
	background-color: ${neutral[97]};
`;
const speechBubbleWrapperStyles = css `
	margin-right: 10px;
`;
const successTextStyles = css `
	${textSans.medium({ fontWeight: 'bold' })}
`;
const summeryStyles = css `
	/* Removing default styles from summery tag */
	::-webkit-details-marker {
		display: none;
	}
	outline: none;

	/* We don't want the summary to open when we click anything but the button, so we pointer-event: none the summary */
	/* 176da211-05aa-4280-859b-1e3157b3f19e */
	pointer-events: none;

	/*
        why hide visibility?
        because we want to prevent the user for tabbing to the summery HTML element
        without using tabIndex={-1} which would disable focus on all child DOM elements

        NOTE: requires "visibility: visible;" on child elements to display and enable focus
    */
	visibility: hidden;

	a {
		/* but we do want to allow click on links */
		pointer-events: all;
	}
`;
const summeryContentWrapper = css `
	visibility: visible;
	min-height: 70px;
	display: flex;
	flex-direction: row;
`;
const speechBubbleStyles = (palette) => css `
	${textSans.medium({ fontWeight: 'bold' })}
	color: ${neutral[100]};
	background-color: ${palette.background.speechBubble};
	min-width: 88px;
	padding-bottom: 6px;
	padding-left: 10px;
	padding-right: 10px;
	::after {
		content: '';
		width: 20px;
		height: 22px;
		border-bottom-right-radius: 18px;
		position: absolute;
		background-color: ${palette.background.speechBubble};
	}
`;
const headingTextHeaderStyles = css `
	${textSans.medium({ fontWeight: 'bold' })}
`;
const headingTextStyles = (palette) => css `
	a {
		color: ${palette.text.calloutHeading};
		text-decoration: none;
		:hover {
			text-decoration: underline;
		}
	}
`;
const buttonWrapperStyles = css `
	position: absolute;
	cursor: pointer;
	margin-top: -5px;

	visibility: visible;

	/* We need to ensure our pointer-events are turned back on on the button */
	/* 176da211-05aa-4280-859b-1e3157b3f19e */
	pointer-events: all;
`;
// Normally forms are in Modals, but here they are embeded into the page
// we therefore need to only focus on expandFormButtonRef if the form has been closed
// after it was opened
let hasFormBeenOpened = true;
export const CalloutBlockComponent = ({ callout, format, }) => {
    let expandFormButtonRef = null;
    let firstFieldElementRef = null;
    let lastElementRef = null;
    const [isExpanded, setIsExpanded] = useState(false);
    const [error, setError] = useState('');
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const palette = decidePalette(format);
    const { title, description, formFields } = callout;
    const onSubmit = async (formData) => {
        // Reset error for new submission attempt
        setError('');
        if (formData.twitterHandle) {
            setError('Sorry we think you are a robot.');
            return;
        }
        // need to add prefix `field_` to all keys in form
        const formDataWithFieldPrefix = Object.keys(formData).reduce((acc, cur) => ({
            ...acc,
            [`field_${cur}`]: formData[cur],
        }), {});
        return fetch(callout.calloutsUrl, {
            method: 'POST',
            body: JSON.stringify({
                formId: callout.formId,
                // TODO: check if we need to send this
                'twitter-handle': '',
                ...formDataWithFieldPrefix,
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        })
            .then((resp) => {
            if (resp.status === 201) {
                setSubmissionSuccess(true);
                setIsExpanded(false);
            }
            else {
                setError('Sorry, there was a problem submitting your form. Please try again later.');
            }
        })
            .catch((respError) => {
            window.guardian.modules.sentry.reportError(respError, 'callout-embed-submission');
            setError('Sorry, there was a problem submitting your form. Please try again later.');
        });
    };
    // ***************************
    // *     Accessibility       *
    // ***************************
    useEffect(() => {
        // we have to use document.querySelector to find DOM elements
        // as Source library does not yet support react ref
        // TODO: use `useRef` once supported in Source
        // eslint-disable-next-line react-hooks/exhaustive-deps
        expandFormButtonRef = document.querySelector('button[custom-guardian="callout-form-open-button"]');
        // eslint-disable-next-line react-hooks/exhaustive-deps
        firstFieldElementRef = document.querySelector(`
            *[custom-guardian="callout-form-field"]:first-of-type input,
            *[custom-guardian="callout-form-field"]:first-of-type select
        `);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        lastElementRef = document.querySelector('button[custom-guardian="callout-form-close-button"]');
        // lock shift to the form
        const keyListener = (e) => {
            // keyCode 9 is tab key
            if (e.keyCode === 9) {
                // If firstElement or lastElement are not defined, do not continue
                if (!firstFieldElementRef || !lastElementRef)
                    return;
                // we use `e.shiftKey` internally to determin the direction of the highlighting
                // using document.activeElement and e.shiftKey we can check what should be the next element to be highlighted
                if (!e.shiftKey && document.activeElement === lastElementRef) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    firstFieldElementRef && firstFieldElementRef.focus();
                    e.preventDefault();
                }
                if (e.shiftKey &&
                    document.activeElement === firstFieldElementRef) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    lastElementRef && lastElementRef.focus(); // The shift key is down so loop focus back to the last item
                    e.preventDefault();
                }
            }
        };
        document.addEventListener('keydown', keyListener);
        return () => document.removeEventListener('keydown', keyListener);
    }, [isExpanded]);
    // on open form, focus on firstFieldElementRef
    useEffect(() => {
        if (isExpanded && firstFieldElementRef) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            firstFieldElementRef && firstFieldElementRef.focus();
        }
    }, [isExpanded, firstFieldElementRef]);
    // on close form, focus on expandFormButtonRef
    useEffect(() => {
        if (!isExpanded && expandFormButtonRef && !hasFormBeenOpened) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            expandFormButtonRef && expandFormButtonRef.focus();
        }
    }, [isExpanded, expandFormButtonRef]);
    // Normally forms are in Modals, but here they are embeded into the page
    // we therefore need to only focus on expandFormButtonRef if the form has been closed
    // after it was opened
    useEffect(() => {
        if (isExpanded) {
            hasFormBeenOpened = false;
        }
    }, [isExpanded]);
    // be able to close the form using the escape key for accessibility
    useEffect(() => {
        const keyListener = (e) => {
            // keyCode 27 is the escape key, we want to be able to close the form using it
            if (e.keyCode === 27) {
                setIsExpanded(false);
            }
        };
        if (isExpanded) {
            document.addEventListener('keydown', keyListener);
        }
        return () => document.removeEventListener('keydown', keyListener);
    }, [isExpanded, setIsExpanded]);
    if (submissionSuccess) {
        return (_jsx("figure", Object.assign({ "data-print-layout": "hide", css: wrapperStyles }, { children: _jsx("details", Object.assign({ css: [calloutDetailsStyles, backgroundColorStyle], "aria-hidden": true, open: isExpanded }, { children: _jsx("summary", Object.assign({ css: summeryStyles }, { children: _jsxs("div", Object.assign({ css: summeryContentWrapper }, { children: [_jsx("div", Object.assign({ css: speechBubbleWrapperStyles }, { children: _jsx("div", Object.assign({ css: speechBubbleStyles(palette) }, { children: _jsx("h4", { children: "Share your story" }, void 0) }), void 0) }), void 0), _jsx("div", Object.assign({ css: headingTextStyles(palette) }, { children: _jsx("p", Object.assign({ css: successTextStyles }, { children: "Thank you for your contribution" }), void 0) }), void 0)] }), void 0) }), void 0) }), void 0) }), void 0));
    }
    return (_jsx("figure", Object.assign({ "data-print-layout": "hide", css: wrapperStyles }, { children: _jsxs("details", Object.assign({ css: [calloutDetailsStyles, isExpanded && backgroundColorStyle], "aria-hidden": true, open: isExpanded }, { children: [_jsxs("summary", Object.assign({ css: summeryStyles }, { children: [_jsxs("div", Object.assign({ css: summeryContentWrapper }, { children: [_jsx("div", Object.assign({ css: speechBubbleWrapperStyles }, { children: _jsx("div", Object.assign({ css: speechBubbleStyles(palette) }, { children: _jsx("h4", { children: "Share your story" }, void 0) }), void 0) }), void 0), _jsxs("div", Object.assign({ css: headingTextStyles(palette) }, { children: [_jsx("h4", Object.assign({ css: headingTextHeaderStyles }, { children: title }), void 0), description && (_jsx("div", { dangerouslySetInnerHTML: {
                                                __html: description,
                                            } }, void 0))] }), void 0)] }), void 0), !isExpanded && (_jsx("span", Object.assign({ css: buttonWrapperStyles, "aria-hidden": "true" }, { children: _jsx(Button, Object.assign({ css: css `
									/* TODO: need to find an nicer way of dynamically setting svg dimensions */
									svg {
										width: 15px !important;
										height: 15px !important;
									}
								`, iconSide: "left", size: "xsmall", icon: _jsx(PlusIcon, {}, void 0), onClick: () => setIsExpanded(true), "custom-guardian": "callout-form-open-button", tabIndex: 0 }, { children: "Tell us" }), void 0) }), void 0))] }), void 0), _jsx(Form, { formFields: formFields, onSubmit: onSubmit, error: error }, void 0), _jsx("span", Object.assign({ css: buttonWrapperStyles, "aria-hidden": "true" }, { children: isExpanded && (_jsx(Button, Object.assign({ iconSide: "left", size: "xsmall", icon: _jsx(MinusIcon, {}, void 0), onClick: () => setIsExpanded(false), "custom-guardian": "callout-form-close-button" }, { children: "Hide" }), void 0)) }), void 0)] }), void 0) }), void 0));
};
